import { useEffect, useState } from "react";
import { api, inr } from "../../lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Building2, Copy, Trash2, ShieldCheck, KeyRound, CheckCircle2, Inbox, ArrowRight, FileText } from "lucide-react";

const EMPTY = {
  email: "", password: "", name: "", business_name: "", gstin: "",
  phone: "", address_line1: "", city: "", state: "", pincode: "",
  payment_terms: "", notes: "",
};

export default function AdminWholesalers() {
  const [rows, setRows] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const [bank, setBank] = useState({ bank_name:"", account_name:"", account_number:"", ifsc:"", branch:"", upi_id:"" });
  const [savingBank, setSavingBank] = useState(false);

  const load = () => {
    api.get("/admin/wholesalers").then(r => setRows(r.data)).catch(()=>{});
    api.get("/admin/wholesale/enquiries").then(r => setEnquiries(r.data)).catch(()=>{});
  };
  useEffect(() => {
    load();
    api.get("/admin/company-bank").then(r=>setBank(b => ({ ...b, ...(r.data || {}) }))).catch(()=>{});
  }, []);

  const convertEnquiry = (e) => {
    setForm({ ...EMPTY, name: e.name, business_name: e.business_name,
              email: e.email, phone: e.phone, gstin: e.gstin || "",
              city: e.city, notes: e.message });
    setOpen(true);
  };
  const enqStatus = async (id, status) => {
    await api.post(`/admin/wholesale/enquiries/${id}/status`, { status });
    load();
  };
  const enqDelete = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/admin/wholesale/enquiries/${id}`);
    toast.success("Deleted"); load();
  };

  const upd = (k,v) => setForm(f => ({...f, [k]: v}));

  const save = async () => {
    if (!form.email || !form.name || !form.business_name) {
      toast.error("Email, contact name and business name are required");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/admin/wholesalers", form);
      setTempPw({ email: form.email, pw: r.data.temp_password, verified: r.data.gstin_verification?.verified });
      toast.success(`Seller ${form.business_name} created`);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to invite");
    }
    setSaving(false);
  };

  const del = async (u) => {
    if (!window.confirm(`Delete seller ${u.business_name}?`)) return;
    await api.delete(`/admin/wholesalers/${u.user_id}`);
    toast.success("Deleted");
    load();
  };

  const downloadStatement = (u) => {
    const month = window.prompt("Statement month? Enter YYYY-MM or leave blank for all-time",
      new Date().toISOString().slice(0, 7));
    if (month === null) return; // cancelled
    const q = month.trim() ? `?month=${month.trim()}` : "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/sellers/${u.user_id}/statement.pdf${q}`;
    window.open(url, "_blank");
  };

  const suspend = async (u) => {
    const next = u.wholesale_status === "suspended" ? "active" : "suspended";
    await api.put(`/admin/wholesalers/${u.user_id}`, { wholesale_status: next });
    toast.success(next === "suspended" ? "Suspended" : "Reactivated");
    load();
  };

  const saveBank = async () => {
    setSavingBank(true);
    try {
      await api.post("/admin/company-bank", bank);
      toast.success("Bank details saved — shown on seller checkout");
    } catch (e) { toast.error("Save failed"); }
    setSavingBank(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-outfit font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#D4AF37]"/> Sellers
          </h1>
          <div className="text-sm text-stone-500">{rows.length} accounts · manage seller logins + B2B pricing</div>
        </div>
        <button onClick={()=>{ setForm(EMPTY); setOpen(true); }} data-testid="new-wholesaler-btn"
                className="bg-[#3B2F2F] text-white rounded-md px-4 py-2 flex items-center gap-2 hover:bg-black">
          <Plus className="w-4 h-4"/> Invite seller
        </button>
      </div>

      {enquiries.filter(e => e.status !== "rejected").length > 0 && (
        <div className="bg-[#FEF3E2] border border-[#D4AF37]/40 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Inbox className="w-5 h-5 text-[#D4AF37]"/>
            <span className="font-outfit font-semibold">Enquiries — {enquiries.filter(e=>e.status==="new").length} new</span>
          </div>
          <div className="space-y-2">
            {enquiries.filter(e => e.status !== "rejected").slice(0,10).map(e => (
              <div key={e.enquiry_id} data-testid={`enq-row-${e.email}`}
                   className="bg-white border border-stone-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[240px]">
                  <div className="font-medium">{e.business_name} <span className="text-xs text-stone-500">({e.name})</span></div>
                  <div className="text-xs text-stone-500">{e.email} · {e.phone} {e.city && `· ${e.city}`} {e.gstin && `· GSTIN ${e.gstin}`}</div>
                  {e.monthly_volume && <div className="text-xs text-[#8B7355]">Volume: {e.monthly_volume}</div>}
                  {e.message && <div className="text-xs text-stone-600 mt-1 italic">"{e.message}"</div>}
                </div>
                <span className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 ${
                    e.status === "new" ? "bg-[#E8EDDF] text-[#2E4A35]" :
                    e.status === "contacted" ? "bg-blue-100 text-blue-700" :
                    "bg-stone-200 text-stone-600"}`}>{e.status}</span>
                <div className="flex gap-1">
                  <button onClick={()=>convertEnquiry(e)} data-testid={`enq-approve-${e.email}`}
                          className="text-xs bg-[#3B2F2F] text-white rounded-full px-3 py-1 flex items-center gap-1 hover:bg-black">
                    <ArrowRight className="w-3 h-3"/> Convert
                  </button>
                  <button onClick={()=>enqStatus(e.enquiry_id, "contacted")}
                          className="text-xs border border-stone-300 rounded-full px-3 py-1 hover:bg-stone-50">
                    Mark contacted
                  </button>
                  <button onClick={()=>enqStatus(e.enquiry_id, "rejected")}
                          className="text-xs text-red-600 hover:bg-red-50 rounded-full px-2">Reject</button>
                  <button onClick={()=>enqDelete(e.enquiry_id)} className="text-stone-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden mb-8">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Business</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>GSTIN</TableHead>
            <TableHead>Terms</TableHead>
            <TableHead className="text-right">Orders</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map(u => (
              <TableRow key={u.user_id} data-testid={`wholesaler-row-${u.email}`}>
                <TableCell>
                  <div className="font-medium">{u.business_name || u.name}</div>
                  <div className="text-xs text-stone-500">{u.name}</div>
                </TableCell>
                <TableCell className="text-xs">
                  <div>{u.email}</div>
                  <div className="text-stone-500">{u.phone || "—"}</div>
                </TableCell>
                <TableCell className="font-mono text-xs">
                  <div className="flex items-center gap-1">
                    {u.gstin} {u.gstin_verified && <ShieldCheck className="w-3 h-3 text-[#2E4A35]" title="GSTIN verified"/>}
                  </div>
                </TableCell>
                <TableCell className="text-xs text-stone-600">{u.payment_terms || "—"}</TableCell>
                <TableCell className="text-right font-outfit font-semibold">{u.orders || 0}</TableCell>
                <TableCell>
                  <button onClick={()=>suspend(u)}
                          className={`text-xs rounded-full px-3 py-1 ${u.wholesale_status === "suspended" ? "bg-stone-200 text-stone-600" : "bg-[#E8EDDF] text-[#2E4A35]"}`}>
                    {u.wholesale_status === "suspended" ? "Suspended" : "Active"}
                  </button>
                </TableCell>
                <TableCell>
                  <button onClick={()=>downloadStatement(u)}
                          data-testid={`seller-statement-${u.email}`}
                          title="Download monthly statement PDF"
                          className="text-[#8B5E3C] hover:bg-[#FEF3E2] rounded-md p-1.5 inline-flex mr-1">
                    <FileText className="w-4 h-4"/>
                  </button>
                  <button onClick={()=>del(u)} className="text-stone-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-stone-500 py-10">No sellers yet. Click "Invite seller" to add one.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Bank details for bank-transfer checkout */}
      <div className="bg-white border border-stone-200 rounded-2xl p-6 max-w-3xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-outfit font-semibold text-lg flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#D4AF37]"/> Company bank details</div>
            <div className="text-xs text-stone-500">Shown on the seller checkout page when a buyer picks "Bank transfer".</div>
          </div>
          <button onClick={saveBank} disabled={savingBank}
                  className="bg-[#3B2F2F] text-white rounded-md px-4 py-2 text-sm hover:bg-black disabled:bg-stone-400">
            {savingBank ? "Saving…" : "Save"}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Bank name" value={bank.bank_name} onChange={e=>setBank({...bank, bank_name:e.target.value})}/>
          <Input placeholder="Account holder name" value={bank.account_name} onChange={e=>setBank({...bank, account_name:e.target.value})}/>
          <Input placeholder="Account number" value={bank.account_number} onChange={e=>setBank({...bank, account_number:e.target.value})}/>
          <Input placeholder="IFSC" value={bank.ifsc} onChange={e=>setBank({...bank, ifsc:e.target.value.toUpperCase()})} className="font-mono"/>
          <Input placeholder="Branch" value={bank.branch} onChange={e=>setBank({...bank, branch:e.target.value})}/>
          <Input placeholder="UPI ID (optional)" value={bank.upi_id} onChange={e=>setBank({...bank, upi_id:e.target.value})}/>
        </div>
      </div>

      {/* New seller dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Invite a seller</DialogTitle>
            <DialogDescription>Create a seller login. They sign in at /wholesale with the temp password shown after creation.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Contact name" value={form.name} onChange={e=>upd("name", e.target.value)} data-testid="wh-name"/>
            <Input placeholder="Business / Company name" value={form.business_name} onChange={e=>upd("business_name", e.target.value)} data-testid="wh-business"/>
            <Input placeholder="Email (login id)" value={form.email} onChange={e=>upd("email", e.target.value.toLowerCase())} data-testid="wh-email"/>
            <Input placeholder="Password (leave blank to auto-generate)" value={form.password} onChange={e=>upd("password", e.target.value)} data-testid="wh-password"/>
            <Input placeholder="Phone" value={form.phone} onChange={e=>upd("phone", e.target.value)} data-testid="wh-phone"/>
            <Input placeholder="GSTIN (optional, 15 chars)" value={form.gstin} onChange={e=>upd("gstin", e.target.value.toUpperCase())} data-testid="wh-gstin" className="font-mono"/>
            <div className="col-span-2">
              <Input placeholder="Address line" value={form.address_line1} onChange={e=>upd("address_line1", e.target.value)}/>
            </div>
            <Input placeholder="City" value={form.city} onChange={e=>upd("city", e.target.value)}/>
            <Input placeholder="State" value={form.state} onChange={e=>upd("state", e.target.value)}/>
            <Input placeholder="Pincode" value={form.pincode} onChange={e=>upd("pincode", e.target.value)}/>
            <Input placeholder="Payment terms (e.g. Net 15)" value={form.payment_terms} onChange={e=>upd("payment_terms", e.target.value)}/>
            <div className="col-span-2">
              <Textarea placeholder="Internal notes (optional)" value={form.notes} onChange={e=>upd("notes", e.target.value)}/>
            </div>
          </div>
          <button onClick={save} disabled={saving} data-testid="save-wholesaler"
                  className="w-full mt-4 bg-[#3B2F2F] text-white rounded-md py-2.5 font-medium hover:bg-black disabled:bg-stone-400">
            {saving ? "Creating…" : "Create seller"}
          </button>
        </DialogContent>
      </Dialog>

      {/* Temp password success dialog */}
      <Dialog open={!!tempPw} onOpenChange={(v)=>!v && setTempPw(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-[#2E4A35]"/> Seller created</DialogTitle>
            <DialogDescription>Share these credentials with the buyer. They can change the password after login.</DialogDescription>
          </DialogHeader>
          <div className="bg-[#FEF3E2] border border-[#D4AF37]/40 rounded-lg p-4 space-y-2">
            <div className="text-xs uppercase tracking-widest text-stone-500">Login URL</div>
            <div className="font-mono text-sm break-all">{window.location.origin}/wholesale/login</div>
            <div className="text-xs uppercase tracking-widest text-stone-500 pt-2">Email</div>
            <div className="font-mono">{tempPw?.email}</div>
            <div className="text-xs uppercase tracking-widest text-stone-500 pt-2">Temp password</div>
            <div className="font-mono text-lg text-[#3B2F2F] flex items-center gap-2">
              {tempPw?.pw}
              <button onClick={()=>{ navigator.clipboard.writeText(tempPw.pw); toast.success("Copied"); }}
                      className="text-stone-500 hover:text-[#3B2F2F]"><Copy className="w-4 h-4"/></button>
            </div>
            {tempPw && !tempPw.verified && <div className="text-[11px] text-stone-500 mt-2">Note: GSTIN not live-verified (no GST_API_KEY configured) — format check only.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
